import { useCallback, useMemo, useState } from 'react'
import { isBrowser } from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import { logger } from '@/lib/logger'

function hasLocalStorageAccess() {
  if (['test', 'development'].includes(process.env.NODE_ENV)) return true

  try {
    const testKey = '__test__'

    localStorage.setItem(testKey, 'test')
    localStorage.removeItem(testKey)

    return true
  } catch (_) {
    return false
  }
}

function buildStorageKey({
  account,
  workspace,
  key,
}: {
  account: string
  workspace: string
  key: string
}) {
  const parsedWorkspace = workspace ? `${workspace}--` : ''

  return `${parsedWorkspace}${account}--${key}`
}

type SetValue<T> = T | ((currentValue: T) => T)

export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T) => void] {
  const { account, workspace } = useAdmin()

  const keyWithAccountAndWorkspace = useMemo(
    () => buildStorageKey({ account, workspace, key }),
    [account, key, workspace]
  )

  const [stateValue, setStateValue] = useState<T>(() => {
    if (!isBrowser || !hasLocalStorageAccess()) {
      return initialValue
    }

    try {
      const item = localStorage.getItem(keyWithAccountAndWorkspace)

      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      logger({
        account,
        type: 'warn',
        error: {
          ...(error as Error),
          message: `Error reading localStorage key ${key}`,
        },
      })
      localStorage.removeItem(keyWithAccountAndWorkspace)

      return initialValue
    }
  })

  const setValue = useCallback(
    (value: SetValue<T>) => {
      try {
        const newValue = value instanceof Function ? value(stateValue) : value
        setStateValue(newValue)

        if (isBrowser && hasLocalStorageAccess()) {
          localStorage.setItem(
            keyWithAccountAndWorkspace,
            JSON.stringify(newValue)
          )
        }
      } catch (error) {
        logger({
          account,
          type: 'warn',
          error: {
            ...(error as Error),
            message: `Error setting localStorage key ${key}`,
          },
        })
        setStateValue(value)
      }
    },
    [account, key, keyWithAccountAndWorkspace, stateValue]
  )

  return [stateValue, setValue]
}
